import { Carriers } from '../constants/carriers';
import { Products } from '../constants/products';
import { UserOrganization } from '../constants/userOrganization';
import { AmeritasChoice } from './ameritasChoice';
import { AmeritasIUL } from './ameritasIUL';
import { AmeritasSI } from './ameritasSI';
import { LgaElevatedRisk } from './LGAElevatedRisk';
import { LgaTerm } from './LGATerm';
import { NorthAmericanNAIULACUM } from './northAmericanNAIULACUM';
import { NorthAmericanNAIULACUMJuvenile } from './northAmericanNAIULACUMJuvenile';
import { ProductConfig } from './productConfig';
import { ProtectiveSpirit } from './ProtectiveSpirit';
import { SeniorLifeGIWL } from './seniorLifeGIWL';
import { SeniorLifeSIWL } from './seniorLifeSIWL';

const carrierProductToKey = (carrier: Carriers, product: Products) =>
  carrier + '-' + product;
const carrierProductSubCategoryToKey = (
  carrier: Carriers,
  product: Products,
  subCategory: string,
) => carrier + '-' + product + '-' + subCategory;
const productConfigs = new Map<string, ProductConfig>();

/**
 * Adds a product config to the registry.
 * For products with subCategory, uses carrier-product-subCategory key.
 * For base products (no subCategory), uses carrier-product key.
 */
const addProductToConfig = (product: ProductConfig) => {
  const subCategory = product.getSubCategory();
  if (subCategory) {
    productConfigs.set(
      carrierProductSubCategoryToKey(
        product.getCarrier(),
        product.getProduct(),
        subCategory,
      ),
      product,
    );
  } else {
    productConfigs.set(
      carrierProductToKey(product.getCarrier(), product.getProduct()),
      product,
    );
  }
};

addProductToConfig(new LgaTerm());
addProductToConfig(new LgaElevatedRisk());
addProductToConfig(new AmeritasIUL());
addProductToConfig(new AmeritasChoice());
addProductToConfig(new AmeritasSI());
addProductToConfig(new SeniorLifeGIWL());
addProductToConfig(new SeniorLifeSIWL());
addProductToConfig(new ProtectiveSpirit());
addProductToConfig(new NorthAmericanNAIULACUM());
addProductToConfig(new NorthAmericanNAIULACUMJuvenile()); // Sahara Juvenile variant

export class ProductFactory {
  /**
   * Gets product config by carrier, product, and optional subCategory.
   * If subCategory is provided, looks up carrier-product-subCategory first.
   * Falls back to carrier-product if subCategory lookup fails.
   */
  public static getProductConfig(
    carrier: string,
    product: string,
    subCategory: string,
  ): ProductConfig | undefined {
    if (subCategory) {
      return productConfigs.get(
        carrierProductSubCategoryToKey(
          carrier as Carriers,
          product as Products,
          subCategory,
        ),
      );
    }
    return productConfigs.get(
      carrierProductToKey(carrier as Carriers, product as Products),
    );
  }
  public static getCarriersByOrganization(org: UserOrganization): Carriers[] {
    const result: Carriers[] = [];
    productConfigs.forEach(product => {
      if (product.availableToOrganization(org)) {
        if (!result.includes(product.getCarrier())) {
          result.push(product.getCarrier());
        }
      }
    });
    return result;
  }
  public static getProductsByOrganization(
    org: UserOrganization,
  ): ProductConfig[] {
    const result: ProductConfig[] = [];
    productConfigs.forEach(product => {
      if (product.availableToOrganization(org)) {
        result.push(product);
      }
    });
    return result;
  }
}
